import React, { memo } from 'react';
import { Paper, Text, Group, Alert, Title } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

/**
 * Represents a single statistic item with label and numeric value.
 */
interface StatItem {
    /** Display label for the statistic */
    label: string;
    /** Numeric value of the statistic */
    value: number;
}

/**
 * Props for the IntraStats component.
 */
interface IntraStatsProps {
    /** Array of statistics to display in the stats bar */
    stats: StatItem[];
    /** Optional announcement/memo text to display for organizers */
    announcement?: string | null;
}

/**
 * Displays a statistics overview bar for the Intra admin dashboard.
 *
 * Shows key metrics in a horizontal layout:
 * - Active registrations count
 * - Archived registrations count
 * - Meal orders count (if food feature enabled)
 * - Pending invoices count
 * - Unsent invitations count
 *
 * Optionally displays an organizer announcement/memo below the stats.
 *
 * @param props - The component props
 * @returns A stats bar with optional announcement alert
 */
const IntraStats: React.FC<IntraStatsProps> = ({ stats, announcement }) => {
    return (
        <Paper shadow="sm" p="md" radius="md" withBorder mb="md">
            <Group grow>
                {stats.map(stat => (
                    <div key={stat.label} style={{ textAlign: 'center' }}>
                        <Text size="xl" fw={700}>{stat.value}</Text>
                        <Text size="xs" c="dimmed">{stat.label}</Text>
                    </div>
                ))}
            </Group>
            {announcement && (
                <Alert icon={<IconInfoCircle size={16} />} color="blue" style={{ marginTop: '1rem' }}>
                    <Title order={5} mb="xs">Järjestäjien muistio</Title>
                    {announcement}
                </Alert>
            )}
        </Paper>
    );
};

export default memo(IntraStats);

